/* eslint-disable no-undef */
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons/faCircleInfo';
import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons/faExclamationCircle';
import { faHomeLgAlt } from '@fortawesome/pro-regular-svg-icons/faHomeLgAlt';
import { MortgageCalcData } from '@propertypal/shared/src/hooks/useMortgageCalculator';
import { trackEnquiryClick, trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { Property } from '@propertypal/shared/src/types/property';
import getContact from '@propertypal/shared/src/utils/enquiries/getContact';
import { getAgentLogos } from '@propertypal/shared/src/utils/property';
import getSellingAgent, { getAgentColours } from '@propertypal/shared/src/utils/property/getSellingAgent';
import Link from 'next/link';
import router from 'next/router';
import React, { CSSProperties, FunctionComponent } from 'react';
import { useTheme } from 'styled-components';
import Button from '../buttons/Button';
import WhiteButton from '../buttons/WhiteButton';
import HorizontalDivider from '../divider/HorizontalDivider';
import Chevron from '../icons/Chevron';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { RowWrap } from '../layout';
import { Text, LinkText, TextLink, SubHeading } from '../typography';
import {
  AgentBox,
  AgentLogo,
  AgentText,
  LicenceWarning,
  AgentBoxHeader,
  AgentExtraInfo,
  AgentListItem,
  IconBox,
  AgentButtons,
  AgentBoxInner,
  BrokerBox,
  Divider,
} from './PropertyAgentBox.style';
import PropertyAgentBoxFixed from './PropertyAgentBoxFixed';
import PropertyPhoneNumberButton from './PropertyPhoneNumberButton';

export interface Props {
  containerStyle?: CSSProperties;
  property: Property;
  onCallClick: (location: string) => void;
  onShowNumberClick: (location: string, accountNumber?: string) => void;
  onEnquiryClick: (accountNumber?: string) => void;
  onRegisterInterestClick: () => void;
  registerInterest?: boolean;
  leadId: number;
  mortgageCalculator: MortgageCalcData;
}

const PropertyAgentBox: FunctionComponent<Props> = (props) => {
  const { property } = props;
  const theme = useTheme();
  const sellingAgent = getSellingAgent(property);
  const {
    pdpBrandingBgColour,
    pdpBrandingBorderColour,
    pdpBrandingButtonBgColour,
    pdpBrandingButtonTextColour,
    pdpBrandingLinkColour,
  } = getAgentColours(property.enhancedBranding, sellingAgent);
  const accountContact = property.contacts?.all.find((c) => c.accountNumber === property.account.accountNumber);
  const contactInfo = getContact(property);
  const page = property.type === 'development' ? 'DDP' : 'PDP';
  const logos = getAgentLogos(props.property);
  const { broker } = props.mortgageCalculator;
  const leadsLink = property.leadIds?.length
    ? `/my/enquiries?leadId=${property.leadIds[0]}`
    : props.leadId
    ? `/my/enquiries?leadId=${props.leadId}`
    : '';

  const enquireNowClick = (location: string, accountNumber?: string) => {
    trackEnquiryClick(page, location, property);
    props.onEnquiryClick(accountNumber);
  };

  const registerInterestClick = (location: string) => {
    trackGaEvent('register_interest_click', { page, location });
    props.onRegisterInterestClick();
  };

  return (
    <>
      <AgentBox color={pdpBrandingBorderColour}>
        {property.developerAsPrimaryBrand && (
          <div style={{ marginBottom: 30 }}>
            <AgentBoxHeader>
              <SubHeading align="center" fontSize={20} mb={15}>
                Developed By
              </SubHeading>
              <AgentLogo data-testid="agent-logo" src={property.account.logo?.standard} alt="Agent Logo" />
            </AgentBoxHeader>

            {(accountContact?.email || accountContact?.phone) && (
              <AgentBoxInner>
                <div>
                  {accountContact?.email && !leadsLink && (
                    <Button
                      containerStyle={{ height: 50, marginBottom: 15 }}
                      onClick={() => enquireNowClick('Enquiry Box', property.account.accountNumber)}
                      testID="enquireNowButton"
                      type="button"
                      color={pdpBrandingButtonBgColour}
                      fontColor={pdpBrandingButtonTextColour}
                    >
                      Email the Developer
                    </Button>
                  )}

                  {accountContact?.phone && (
                    <PropertyPhoneNumberButton
                      {...props}
                      containerStyle={{ height: 50, margin: '0 auto 15px' }}
                      location="Enquiry Box"
                      accountNumber={property.account.accountNumber}
                      useShortText={false}
                      suffix="Developer"
                    />
                  )}
                </div>
              </AgentBoxInner>
            )}

            <Divider />
          </div>
        )}

        {sellingAgent && !property.developerAsPrimaryBrand && (
          <AgentBoxHeader color={pdpBrandingBgColour}>
            <Link href={sellingAgent.landingPageUrl} prefetch={false}>
              {sellingAgent.logo && sellingAgent.logo.standard && (
                <AgentLogo data-testid="agent-logo" src={sellingAgent.logo.standard} alt="Agent Logo" />
              )}
            </Link>
          </AgentBoxHeader>
        )}

        <AgentBoxInner>
          <div>
            <SubHeading align="center" fontSize={20} mb={15}>
              Marketed by {sellingAgent ? sellingAgent.organisation : 'multiple agents'}
            </SubHeading>

            {property.developerAsPrimaryBrand && (
              <RowWrap gap={15} mb={15} style={{ justifyContent: 'center' }}>
                {logos.map((logo) => (
                  <AgentLogo
                    data-testid="agent-logo"
                    key={logo.url}
                    src={logo.url}
                    alt="Agent Logo"
                    style={{ maxWidth: `calc(${100 / logos.length}% - 15px)` }}
                  />
                ))}
              </RowWrap>
            )}

            {sellingAgent && sellingAgent.psrLicenceNumber && property.countryCode === 'IRL' && (
              <AgentText>
                PSR Licence: {sellingAgent.psrLicenceNumber}
                {sellingAgent.psrLicenceNumber === 'NO LICENCE' && (
                  <LicenceWarning href="/psr-licence-number" prefetch={false}>
                    ?
                  </LicenceWarning>
                )}
              </AgentText>
            )}

            <Text className="show-print" align="center" fontSize={18} mb={15}>
              {sellingAgent?.officePhone?.international}
            </Text>

            {!!property.leadIds?.length && (
              <Text
                align="center"
                color={theme.primary}
                fontSize={14}
                fontWeight="600"
                style={{ maxWidth: 240, margin: '0 auto' }}
              >
                <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: 8 }} />
                You&apos;ve already made an enquiry about this property.{' '}
                <Link href={leadsLink} style={{ textDecoration: 'underline' }}>
                  View <Chevron size={10} />
                </Link>
              </Text>
            )}

            {property.published && (
              <AgentButtons className="hide-print">
                {props.registerInterest && !leadsLink && (
                  <>
                    <Button
                      containerStyle={{ height: 50, marginBottom: 15 }}
                      onClick={() => registerInterestClick('Enquiry Box')}
                      testID="registerInterestButton"
                      type="button"
                      color={pdpBrandingButtonBgColour}
                      fontColor={pdpBrandingButtonTextColour}
                    >
                      Register Your Interest
                    </Button>

                    <HorizontalDivider color={theme.textDark} dividerColor={theme.backgroundLight} text="or" mb={15} />
                  </>
                )}

                {!!leadsLink && (
                  <Link href={leadsLink} style={{ width: '100%', marginBottom: 15 }}>
                    <Button
                      containerStyle={{ height: 50 }}
                      type="button"
                      color={pdpBrandingButtonBgColour}
                      fontColor={pdpBrandingButtonTextColour}
                    >
                      Send another message
                    </Button>
                  </Link>
                )}

                {contactInfo.email && !leadsLink && (
                  <Button
                    containerStyle={{ height: 50, marginBottom: 15 }}
                    onClick={() => enquireNowClick('Enquiry Box')}
                    testID="enquireNowButton"
                    type="button"
                    color={pdpBrandingButtonBgColour}
                    fontColor={pdpBrandingButtonTextColour}
                  >
                    Email the Agent
                  </Button>
                )}

                {contactInfo.phone && (
                  <PropertyPhoneNumberButton
                    {...props}
                    containerStyle={{ height: 50, margin: '0 auto 15px' }}
                    location="Enquiry Box"
                    useShortText={false}
                  />
                )}
              </AgentButtons>
            )}
          </div>

          {property.enhancedBranding && sellingAgent && (
            <AgentExtraInfo>
              {!!sellingAgent.displayAddress && (
                <AgentListItem>
                  <IconBox>
                    <FontAwesomeIcon icon={faCircleInfo} color={pdpBrandingLinkColour} style={{ fontSize: 18 }} />
                  </IconBox>

                  {sellingAgent.landingPageUrl && (
                    <TextLink href={sellingAgent.landingPageUrl} fontSize={14} color={pdpBrandingLinkColour}>
                      More information about
                      {sellingAgent.organisation.split(' ').map((v, i, l) =>
                        i + 1 === l.length ? (
                          <span style={{ whiteSpace: 'nowrap' }} key={v}>
                            {' '}
                            {v}
                            <Chevron size={11} />
                          </span>
                        ) : (
                          ` ${v}`
                        ),
                      )}
                    </TextLink>
                  )}
                </AgentListItem>
              )}

              {sellingAgent.searchUrl && (
                <AgentListItem>
                  <IconBox>
                    <FontAwesomeIcon icon={faHomeLgAlt} color={pdpBrandingLinkColour} style={{ fontSize: 18 }} />
                  </IconBox>

                  <Text>
                    <Link href={sellingAgent.searchUrl} prefetch={false}>
                      <LinkText as="span" fontSize={14} color={pdpBrandingLinkColour}>
                        View our similar properties <Chevron size={11} />
                      </LinkText>
                    </Link>
                  </Text>
                </AgentListItem>
              )}
            </AgentExtraInfo>
          )}
        </AgentBoxInner>

        {!!broker && (
          <BrokerBox>
            {broker.defaultLogo && (
              <img
                src={broker.defaultLogo.replace('www-dev.propertypal.net', 'www.propertypal.com')}
                alt={`${broker.name} Broker Calculator`}
              />
            )}

            <WhiteButton onClick={() => router.replace(`${property.path}/broker`, undefined, { shallow: true })}>
              Request Mortgage Advice
            </WhiteButton>
          </BrokerBox>
        )}
      </AgentBox>

      <PropertyAgentBoxFixed
        registerInterestClick={registerInterestClick}
        enquireNowClick={enquireNowClick}
        contactInfo={contactInfo}
        leadsLink={leadsLink}
        sellingAgent={sellingAgent}
        broker={broker}
        {...props}
      />
    </>
  );
};

export default PropertyAgentBox;
